import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import PhotoIcon from "@mui/icons-material/Photo";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import api from "../axiosConfig";

const extractRequestErrorMessage = (requestError, fallbackMessage) => {
  const validationErrors = requestError?.response?.data?.errors;
  if (validationErrors && typeof validationErrors === "object") {
    const firstValidationError = Object.values(validationErrors)
      .flat()
      .find((message) => typeof message === "string" && message.trim());

    if (firstValidationError) {
      return firstValidationError;
    }
  }

  return requestError?.response?.data?.message || requestError?.message || fallbackMessage;
};

export const DEFAULT_WORKSPACE_NOTIFICATION_PREFERENCES = {
  omnichannel: {
    whatsapp: {
      mode: "important_only",
      handover_enabled: true,
      mute_same_conversation_when_active: true,
    },
    personal_mailbox: {
      whatsapp_inbound_enabled: false,
    },
  },
};

export function normalizeWorkspaceNotificationPreferences(value = {}) {
  return {
    omnichannel: {
      whatsapp: {
        ...DEFAULT_WORKSPACE_NOTIFICATION_PREFERENCES.omnichannel.whatsapp,
        ...(value?.omnichannel?.whatsapp || {}),
      },
      personal_mailbox: {
        ...DEFAULT_WORKSPACE_NOTIFICATION_PREFERENCES.omnichannel.personal_mailbox,
        ...(value?.omnichannel?.personal_mailbox || {}),
      },
    },
  };
}

export default function UserWorkspaceSettingsDialog({
  open,
  onClose,
  currentSignatureUrl,
  notificationPreferences,
  onUploaded,
  onPreferencesSaved,
}) {
  const [preview, setPreview] = useState(currentSignatureUrl || null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [draftPreferences, setDraftPreferences] = useState(() =>
    normalizeWorkspaceNotificationPreferences(notificationPreferences)
  );
  const [mailboxStatus, setMailboxStatus] = useState(null);
  const [loadingMailboxStatus, setLoadingMailboxStatus] = useState(false);
  const [workspaceProfile, setWorkspaceProfile] = useState(null);
  const [loadingWorkspaceProfile, setLoadingWorkspaceProfile] = useState(false);
  const [mailboxBusy, setMailboxBusy] = useState(false);
  const [mailboxPassword, setMailboxPassword] = useState("");
  const [mailboxMessage, setMailboxMessage] = useState(null);
  const [activeSection, setActiveSection] = useState("signature");
  const [gmailStatus, setGmailStatus] = useState(null);
  const [loadingGmailStatus, setLoadingGmailStatus] = useState(false);
  const [gmailBusy, setGmailBusy] = useState(false);
  const [gmailMessage, setGmailMessage] = useState(null);

  const normalizedCurrentPreferences = useMemo(
    () => normalizeWorkspaceNotificationPreferences(notificationPreferences),
    [notificationPreferences]
  );
  const normalizedDraftPreferences = useMemo(
    () => normalizeWorkspaceNotificationPreferences(draftPreferences),
    [draftPreferences]
  );
  const preferencesChanged = useMemo(
    () => JSON.stringify(normalizedCurrentPreferences) !== JSON.stringify(normalizedDraftPreferences),
    [normalizedCurrentPreferences, normalizedDraftPreferences]
  );

  const loadMailboxStatus = useCallback(async () => {
    try {
      setLoadingMailboxStatus(true);
      const response = await api.get("/users/me/mailbox");
      setMailboxStatus(response?.data?.data || null);
      setMailboxMessage(null);
    } catch (requestError) {
      console.error(requestError);
      setMailboxStatus(null);
      setMailboxMessage({ severity: "warning", text: "Could not load mailbox status right now." });
    } finally {
      setLoadingMailboxStatus(false);
    }
  }, []);

  const loadWorkspaceProfile = useCallback(async () => {
    try {
      setLoadingWorkspaceProfile(true);
      const response = await api.get("/users/me");
      const profile = response?.data || null;
      setWorkspaceProfile(profile);
      if (profile?.mailbox) {
        setMailboxStatus(profile.mailbox);
      }
      // Use the server's authoritative signature URL.
      // Preserve a local blob URL (user has a pending new upload).
      setPreview((prev) => {
        if (prev && prev.startsWith("blob:")) return prev;
        return profile?.signature_url || null;
      });
      return profile;
    } catch (requestError) {
      console.error(requestError);
      setWorkspaceProfile(null);
      return null;
    } finally {
      setLoadingWorkspaceProfile(false);
    }
  }, []);

  const loadGmailStatus = useCallback(async () => {
    try {
      setLoadingGmailStatus(true);
      const response = await api.get("/omnichannel/email/google/status");
      setGmailStatus(response?.data?.data || null);
      setGmailMessage(null);
    } catch (requestError) {
      console.error(requestError);
      setGmailStatus(null);
      setGmailMessage({ severity: "warning", text: "Could not load Gmail status right now." });
    } finally {
      setLoadingGmailStatus(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    setPreview(currentSignatureUrl || null);
    setFile(null);
    setBusy(false);
    setError("");
    setActiveSection("signature");
    setDraftPreferences(normalizeWorkspaceNotificationPreferences(notificationPreferences));
    setWorkspaceProfile(null);
    setMailboxPassword("");
    setMailboxMessage(null);
    setGmailStatus(null);
    setGmailMessage(null);

    void (async () => {
      await loadWorkspaceProfile();
      void loadMailboxStatus();
      void loadGmailStatus();
    })();
  }, [open, currentSignatureUrl, notificationPreferences, loadMailboxStatus, loadWorkspaceProfile, loadGmailStatus]);

  const pick = (event) => {
    const selectedFile = event.target.files?.[0];
    if (!selectedFile) return;

    setFile(selectedFile);
    const reader = new FileReader();
    reader.onload = () => setPreview(reader.result);
    reader.readAsDataURL(selectedFile);
  };

  const updateWhatsAppPreference = (key, value) => {
    setDraftPreferences((prev) => ({
      ...normalizeWorkspaceNotificationPreferences(prev),
      omnichannel: {
        ...normalizeWorkspaceNotificationPreferences(prev).omnichannel,
        whatsapp: {
          ...normalizeWorkspaceNotificationPreferences(prev).omnichannel.whatsapp,
          [key]: value,
        },
      },
    }));
  };

  const save = async () => {
    if (!file && !preferencesChanged) {
      onClose();
      return;
    }

    try {
      setBusy(true);
      setError("");

      if (preferencesChanged) {
        const preferenceResponse = await api.put("/users/me/notification-preferences", {
          notification_preferences: normalizedDraftPreferences,
        });
        onPreferencesSaved?.(
          preferenceResponse?.data?.notification_preferences || normalizedDraftPreferences
        );
      }

      if (file) {
        const formData = new FormData();
        formData.append("file", file);

        const signatureResponse = await api.post("/users/me/signature", formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        const savedUrl = signatureResponse?.data?.signature_url || null;
        // Update local preview immediately so it persists when dialog reopens
        setPreview(savedUrl);
        setFile(null);
        onUploaded?.(savedUrl);
      }

      onClose();
    } catch (requestError) {
      console.error(requestError);
      setError(extractRequestErrorMessage(requestError, "Could not save your settings right now."));
    } finally {
      setBusy(false);
    }
  };

  const handleMailboxAction = async (kind) => {
    if (!mailboxStatus?.assigned && kind !== "sync") {
      return;
    }

    if ((kind === "test" || kind === "connect") && !mailboxPassword.trim()) {
      setMailboxMessage({ severity: "warning", text: "Enter your mailbox password first." });
      return;
    }

    try {
      setMailboxBusy(true);
      setMailboxMessage(null);

      let response;
      if (kind === "test") {
        response = await api.post("/users/me/mailbox/test", {
          password: mailboxPassword,
        });
      } else if (kind === "connect") {
        response = await api.put("/users/me/mailbox", {
          password: mailboxPassword,
        });
        setMailboxPassword("");
      } else {
        response = await api.post("/users/me/mailbox/sync-now");
      }

      await loadMailboxStatus();
      setMailboxMessage({
        severity: "success",
        text:
          response?.data?.message
          || (kind === "sync" ? "Mailbox synced." : kind === "test" ? "Mailbox test passed." : "Mailbox connected."),
      });
    } catch (requestError) {
      console.error(requestError);
      setMailboxMessage({
        severity: "error",
        text: extractRequestErrorMessage(requestError, "Mailbox action failed."),
      });
    } finally {
      setMailboxBusy(false);
    }
  };

  const handleGmailAction = async (kind) => {
    if (kind === "connect" || kind === "reconnect") {
      try {
        setGmailBusy(true);
        setGmailMessage(null);
        const res = await api.get("/omnichannel/email/google/initiate", {
          params: { context: "omnichannel", return_to: "/omnichannel/inbox?channel=email" },
        });
        window.location.href = res.data.url;
      } catch (requestError) {
        console.error(requestError);
        setGmailMessage({ severity: "error", text: extractRequestErrorMessage(requestError, "Could not start Google sign-in.") });
        setGmailBusy(false);
      }
      return;
    }
    try {
      setGmailBusy(true);
      setGmailMessage(null);
      const response = await api.post("/omnichannel/email/google/sync-now");
      await loadGmailStatus();
      setGmailMessage({ severity: "success", text: response?.data?.message || "Gmail synced." });
    } catch (requestError) {
      console.error(requestError);
      setGmailMessage({ severity: "error", text: extractRequestErrorMessage(requestError, "Sync failed.") });
    } finally {
      setGmailBusy(false);
    }
  };

  const whatsAppPreferences = normalizedDraftPreferences.omnichannel.whatsapp;
  const personalMailboxPreferences = normalizedDraftPreferences.omnichannel.personal_mailbox;
  const hasWhatsAppDeliveryNumber = Boolean(String(workspaceProfile?.whatsapp_number || "").trim());
  const personalMailboxToggleDisabled =
    !mailboxStatus?.assigned
    || loadingWorkspaceProfile
    || (!hasWhatsAppDeliveryNumber && !personalMailboxPreferences.whatsapp_inbound_enabled);
  const personalMailboxToggleHelperText = !mailboxStatus?.assigned
    ? "This toggle will become available once an admin assigns your company mailbox."
    : loadingWorkspaceProfile
    ? "Checking your WhatsApp delivery number..."
    : !hasWhatsAppDeliveryNumber
    ? personalMailboxPreferences.whatsapp_inbound_enabled
      ? "Mailbox alerts are enabled, but they cannot be delivered until your profile has a WhatsApp number."
      : "Add your WhatsApp number to your profile before enabling mailbox alerts."
    : `Mailbox alerts will be sent to ${workspaceProfile.whatsapp_number}.`;
  const NAV_SECTIONS = [
    { id: "signature",     label: "Email Signature",        icon: "✉" },
    { id: "mailbox",       label: "Personal Mailbox",       icon: "📬" },
    { id: "gmail",         label: "Company Gmail",          icon: "📧" },
    { id: "notifications", label: "WhatsApp Notifications", icon: "🔔" },
  ];

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="md"
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: 740,
          borderRadius: "16px",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      {/* ── Header ── */}
      <Box sx={{
        px: 3, py: 2,
        borderBottom: "1px solid rgba(0,0,0,0.08)",
        display: "flex", alignItems: "center", justifyContent: "space-between",
        flexShrink: 0,
      }}>
        <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 17 }}>Settings</Typography>
        <Button onClick={busy ? undefined : onClose} variant="text" sx={{ color: "#6b7280", fontWeight: 600, textTransform: "none", minWidth: 0, px: 1.5 }}>
          Close
        </Button>
      </Box>

      {/* ── Body: nav + content ── */}
      <Box sx={{ display: "flex", flex: 1, minHeight: 0 }}>
        {/* Left nav */}
        <Box sx={{
          width: 200, flexShrink: 0,
          borderRight: "1px solid rgba(0,0,0,0.07)",
          bgcolor: "#fafbfa",
          py: 1.5, px: 1,
          display: "flex", flexDirection: "column", gap: "2px",
        }}>
          {NAV_SECTIONS.map((sec) => (
            <Box
              key={sec.id}
              component="button"
              onClick={() => setActiveSection(sec.id)}
              sx={{
                display: "flex", alignItems: "center", gap: "10px",
                px: "12px", py: "9px",
                borderRadius: "10px", border: "none",
                bgcolor: activeSection === sec.id ? "#e8f5e9" : "transparent",
                color: activeSection === sec.id ? "#16a34a" : "#374151",
                fontWeight: activeSection === sec.id ? 700 : 500,
                fontSize: 13, textAlign: "left", cursor: "pointer", width: "100%",
                "&:hover": { bgcolor: activeSection === sec.id ? "#e8f5e9" : "#f3f4f6" },
                transition: "all 120ms ease",
              }}
            >
              <span style={{ fontSize: 15 }}>{sec.icon}</span>
              {sec.label}
            </Box>
          ))}
        </Box>

        {/* Right content */}
        <Box sx={{ flex: 1, overflowY: "auto", px: 3.5, py: 3, minWidth: 0 }}>

          {/* ══ Email Signature ══════════════════════════════════════ */}
          {activeSection === "signature" && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 15, mb: 0.4 }}>Email Signature</Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload an image (PNG/JPG/GIF/WEBP, up to 4 MB). It will be appended below every email you send.
                </Typography>
              </Box>

              {/* Live email preview */}
              <Box sx={{ border: "1px solid rgba(0,0,0,0.09)", borderRadius: "12px", overflow: "hidden", bgcolor: "#fff" }}>
                <Box sx={{ px: 2, py: 1, borderBottom: "1px solid rgba(0,0,0,0.07)", bgcolor: "#f8f9fa", display: "flex", alignItems: "center", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#fbbf24" }} />
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#34d399" }} />
                  <Typography variant="caption" sx={{ ml: 1, color: "#6b7280", fontWeight: 600, letterSpacing: 0.4 }}>
                    SIGNATURE PREVIEW
                  </Typography>
                </Box>
                <Box sx={{ px: 3, pt: 2, pb: 2.5 }}>
                  <Typography sx={{ fontSize: 14, color: "#374151", lineHeight: 1.7, fontFamily: "'Google Sans', Roboto, Arial, sans-serif", mb: 1.5 }}>
                    Your message will appear here...
                  </Typography>
                  <Box sx={{ borderTop: "1px solid #e5e7eb", pt: 1.5 }}>
                    {preview ? (
                      <img
                        key={preview}
                        src={preview}
                        alt="Email signature"
                        style={{ display: "block", maxWidth: 380, width: "100%", height: "auto" }}
                        onError={(e) => {
                          e.currentTarget.style.display = "none";
                          const fb = e.currentTarget.parentElement?.querySelector(".sig-fallback");
                          if (fb) fb.style.display = "flex";
                        }}
                        onLoad={(e) => {
                          e.currentTarget.style.display = "block";
                          const fb = e.currentTarget.parentElement?.querySelector(".sig-fallback");
                          if (fb) fb.style.display = "none";
                        }}
                      />
                    ) : null}
                    <Box className="sig-fallback" sx={{ display: preview ? "none" : "flex", alignItems: "center", gap: 1, py: 1 }}>
                      <PhotoIcon sx={{ fontSize: 22, color: "#d1d5db" }} />
                      <Typography sx={{ fontSize: 13, color: "#9ca3af" }}>No signature image uploaded yet</Typography>
                    </Box>
                  </Box>
                </Box>
              </Box>

              {/* Upload controls */}
              <Stack direction="row" spacing={1.5} alignItems="center">
                <Button
                  component="label"
                  variant="contained"
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 2.5 }}
                >
                  {preview ? "Replace image" : "Upload signature image"}
                  <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" hidden onChange={pick} />
                </Button>
                {preview && (
                  <Button
                    variant="outlined"
                    color="inherit"
                    sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, color: "#6b7280", borderColor: "#e5e7eb" }}
                    onClick={() => { setPreview(null); setFile(null); }}
                  >
                    Remove
                  </Button>
                )}
              </Stack>
              {file && (
                <Typography variant="caption" sx={{ color: "#16a34a", fontWeight: 600 }}>
                  ✓ New image selected — click Save to apply
                </Typography>
              )}
            </Stack>
          )}

          {/* ══ Personal Mailbox ══════════════════════════════════════ */}
          {activeSection === "mailbox" && (
            <Stack spacing={2.5}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 15, mb: 0.4 }}>Personal Mailbox</Typography>
                  <Typography variant="body2" color="text.secondary">Connect your company email account to the unified inbox.</Typography>
                </Box>
                <Button
                  size="small" variant="outlined"
                  startIcon={loadingMailboxStatus ? <CircularProgress size={13} /> : <RefreshRoundedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => void loadMailboxStatus()}
                  disabled={loadingMailboxStatus}
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "#374151" }}
                >
                  Refresh
                </Button>
              </Box>

              {mailboxMessage ? <Alert severity={mailboxMessage.severity} sx={{ borderRadius: "10px" }}>{mailboxMessage.text}</Alert> : null}

              {!mailboxStatus?.assigned ? (
                <Alert severity="info" sx={{ borderRadius: "10px" }}>
                  An admin needs to assign your company mailbox before you can connect it here.
                </Alert>
              ) : (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, borderRadius: "12px", border: "1px solid rgba(0,0,0,0.10)", bgcolor: "#fafafa" }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body1" fontWeight={700}>{mailboxStatus?.email_address || "Mailbox assigned"}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                            {mailboxStatus?.imap_host
                              ? `${mailboxStatus.imap_host}:${mailboxStatus.imap_port} IMAP / ${mailboxStatus.outbound_label || `${mailboxStatus.smtp_host}:${mailboxStatus.smtp_port} SMTP`}`
                              : "Server details will appear once the mailbox is assigned."}
                          </Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={mailboxStatus?.connected ? "Connected" : mailboxStatus?.requires_reauth ? "Reconnect needed" : "Pending"}
                          color={mailboxStatus?.connected ? "success" : mailboxStatus?.requires_reauth ? "warning" : "default"}
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>
                      {mailboxStatus?.last_synced_at ? (
                        <Typography variant="caption" color="text.secondary">
                          Last sync: {new Date(mailboxStatus.last_synced_at).toLocaleString()}
                        </Typography>
                      ) : null}
                      {mailboxStatus?.last_error ? (
                        <Typography variant="caption" color="error.main">{mailboxStatus.last_error}</Typography>
                      ) : null}
                    </Stack>
                  </Box>

                  <TextField
                    type="password"
                    label="Mailbox password"
                    value={mailboxPassword}
                    onChange={(event) => setMailboxPassword(event.target.value)}
                    helperText="Use the same password you use in Roundcube/cPanel webmail."
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />

                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button variant="outlined" onClick={() => void handleMailboxAction("test")} disabled={mailboxBusy || !mailboxPassword.trim()} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600 }}>
                      Test connection
                    </Button>
                    <Button variant="contained" onClick={() => void handleMailboxAction("connect")} disabled={mailboxBusy || !mailboxPassword.trim()} sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}>
                      {mailboxStatus?.connected ? "Reconnect mailbox" : "Connect mailbox"}
                    </Button>
                    <Button variant="text" onClick={() => void handleMailboxAction("sync")} disabled={mailboxBusy || !mailboxStatus?.connected} sx={{ borderRadius: "10px", textTransform: "none", color: "#6b7280" }}>
                      Sync now
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}

          {/* ══ Company Gmail ════════════════════════════════════════ */}
          {activeSection === "gmail" && (
            <Stack spacing={2.5}>
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 15, mb: 0.4 }}>Company Gmail</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Connect <strong>abisiniyatravels@gmail.com</strong> to receive emails in the unified inbox.
                  </Typography>
                </Box>
                <Button
                  size="small" variant="outlined"
                  startIcon={loadingGmailStatus ? <CircularProgress size={13} /> : <RefreshRoundedIcon sx={{ fontSize: 15 }} />}
                  onClick={() => void loadGmailStatus()}
                  disabled={loadingGmailStatus}
                  sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "#374151" }}
                >
                  Refresh
                </Button>
              </Box>

              {gmailMessage ? <Alert severity={gmailMessage.severity} sx={{ borderRadius: "10px" }}>{gmailMessage.text}</Alert> : null}

              {loadingGmailStatus ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={28} />
                </Box>
              ) : gmailStatus?.connected ? (
                <Stack spacing={2}>
                  <Box sx={{ p: 2, borderRadius: "12px", border: "1px solid rgba(0,0,0,0.10)", bgcolor: "#fafafa" }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography variant="body1" fontWeight={700}>{gmailStatus.google_email}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>Gmail (OAuth 2.0)</Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={gmailStatus.requires_reauth ? "Reconnect needed" : "Connected"}
                          color={gmailStatus.requires_reauth ? "warning" : "success"}
                          sx={{ fontWeight: 700 }}
                        />
                      </Stack>
                      {gmailStatus?.gmail_last_synced_at ? (
                        <Typography variant="caption" color="text.secondary">
                          Last sync: {new Date(gmailStatus.gmail_last_synced_at).toLocaleString()}
                        </Typography>
                      ) : null}
                    </Stack>
                  </Box>
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Button
                      variant="text"
                      onClick={() => void handleGmailAction("sync")}
                      disabled={gmailBusy || gmailStatus.requires_reauth}
                      sx={{ borderRadius: "10px", textTransform: "none", color: "#6b7280" }}
                    >
                      Sync now
                    </Button>
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={() => void handleGmailAction("reconnect")}
                      disabled={gmailBusy}
                      sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 600, borderColor: "#e5e7eb", color: "#374151" }}
                    >
                      Reconnect
                    </Button>
                  </Stack>
                </Stack>
              ) : (
                <Stack spacing={2}>
                  <Alert severity="info" sx={{ borderRadius: "10px" }}>
                    Connect your company Gmail to sync inbound emails into the unified inbox.
                  </Alert>
                  <Box>
                    <Button
                      variant="contained"
                      onClick={() => void handleGmailAction("connect")}
                      disabled={gmailBusy}
                      sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700 }}
                    >
                      Connect with Google
                    </Button>
                  </Box>
                </Stack>
              )}
            </Stack>
          )}

          {/* ══ WhatsApp Notifications ═══════════════════════════════ */}
          {activeSection === "notifications" && (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 800, fontSize: 15, mb: 0.4 }}>WhatsApp Notifications</Typography>
                <Typography variant="body2" color="text.secondary">
                  Urgent, high priority, and escalation alerts are always sent — these settings only control normal inbox alerts.
                </Typography>
              </Box>

              <FormControl fullWidth>
                <InputLabel id="whatsapp-alert-mode-label">WhatsApp alert level</InputLabel>
                <Select
                  labelId="whatsapp-alert-mode-label"
                  value={whatsAppPreferences.mode}
                  label="WhatsApp alert level"
                  onChange={(event) => updateWhatsAppPreference("mode", event.target.value)}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="important_only">Important only — skip repeated message pings</MenuItem>
                  <MenuItem value="all">All replies</MenuItem>
                  <MenuItem value="off">Off for normal alerts</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ border: "1px solid rgba(0,0,0,0.08)", borderRadius: "12px", overflow: "hidden" }}>
                {[
                  {
                    label: "Notify me when a conversation is handed over to me",
                    key: "handover_enabled",
                    checked: Boolean(whatsAppPreferences.handover_enabled),
                    onChange: (v) => updateWhatsAppPreference("handover_enabled", v),
                  },
                  {
                    label: "Mute WhatsApp while I am active in the same conversation",
                    key: "mute_same",
                    checked: Boolean(whatsAppPreferences.mute_same_conversation_when_active),
                    onChange: (v) => updateWhatsAppPreference("mute_same_conversation_when_active", v),
                  },
                  {
                    label: "Notify me on WhatsApp when a new email arrives in my mailbox",
                    key: "mailbox_inbound",
                    checked: Boolean(personalMailboxPreferences.whatsapp_inbound_enabled),
                    disabled: personalMailboxToggleDisabled,
                    helper: personalMailboxToggleHelperText,
                    onChange: (v) => setDraftPreferences((prev) => ({
                      ...normalizeWorkspaceNotificationPreferences(prev),
                      omnichannel: {
                        ...normalizeWorkspaceNotificationPreferences(prev).omnichannel,
                        personal_mailbox: {
                          ...normalizeWorkspaceNotificationPreferences(prev).omnichannel.personal_mailbox,
                          whatsapp_inbound_enabled: v,
                        },
                      },
                    })),
                  },
                ].map((item, idx, arr) => (
                  <Box key={item.key} sx={{ px: 2, py: 1.5, borderBottom: idx < arr.length - 1 ? "1px solid rgba(0,0,0,0.07)" : "none" }}>
                    <FormControlLabel
                      sx={{ m: 0, width: "100%", justifyContent: "space-between" }}
                      labelPlacement="start"
                      control={
                        <Switch
                          checked={item.checked}
                          onChange={(e) => item.onChange(e.target.checked)}
                          disabled={item.disabled}
                          size="small"
                        />
                      }
                      label={
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#111827" }}>{item.label}</Typography>
                          {item.helper && <Typography variant="caption" color="text.secondary">{item.helper}</Typography>}
                        </Box>
                      }
                    />
                  </Box>
                ))}
              </Box>
            </Stack>
          )}

          {error ? <Alert severity="error" sx={{ mt: 2, borderRadius: "10px" }}>{error}</Alert> : null}
        </Box>
      </Box>

      {/* ── Footer ── */}
      <Box sx={{
        px: 3, py: 1.75,
        borderTop: "1px solid rgba(0,0,0,0.08)",
        display: "flex", justifyContent: "flex-end", gap: 1.5,
        flexShrink: 0, bgcolor: "#fafbfa",
      }}>
        <Button onClick={busy ? undefined : onClose} variant="text" disabled={busy} sx={{ textTransform: "none", fontWeight: 600, color: "#6b7280" }}>
          Cancel
        </Button>
        <Button
          onClick={save}
          variant="contained"
          disabled={busy || (!file && !preferencesChanged)}
          sx={{ borderRadius: "10px", textTransform: "none", fontWeight: 700, px: 3, boxShadow: "none" }}
        >
          {busy ? "Saving…" : "Save changes"}
        </Button>
      </Box>
    </Dialog>
  );
}
